import { routes } from 'defs';
import * as React from 'react';

import i18n from 'helper/i18n';
import noop from 'helper/noop';

import Checkbox from 'components/atoms/Checkbox';
import FieldInput from 'components/atoms/FieldInput';
import { isNativePlatformAndroid } from 'components/helper/Platform';
import Spacer from 'components/helper/Spacer';
import Agreement from 'components/molecules/Agreement';
import ButtonBar from 'components/molecules/ButtonBar';
import ElementList from 'components/molecules/ElementList';
import leaveCoach from 'components/organisms/Anamnesis/Components/leave-coach';

import { ButtonBarProps } from 'components/molecules/ButtonBar';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';

import withValidation from 'components/hoc/withValidation';
import sanitize from 'helper/sanitizer';
import { VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_NOT_RECOMMENDED, VALIDATE_IS_PENDING } from 'helper/validator';
import weightValidatorFactory from 'helper/validator/weight';
import { EventType } from 'store/events';

const ControlledInput = withValidation(
	FieldInput,
	weightValidatorFactory,
	[sanitize.maxLength(5), sanitize.stripChars, sanitize.replaceCommasWithDots],
	{
		shouldValidateInitial: true,
	}
);

export type DetermineWeightProps = {
	actionButtonsActive: ButtonBarProps;
	actionButtonsInactive: ButtonBarProps;
	isUpdatingAnamnesis: boolean;
	inputField: any;
	weight?: number;
	onNavigationClick: (targetUrl: { href: string }) => void /* parent */;
	onStepCompleted: (resolver: AnamnesisStepResolver) => void /* parent */;
	doAddEvents: (events: EventType[]) => { type: 'EVENTS/ADD'; events: EventType[] } /* events */;
	doSaveWeight: Function /* actionCreator */;
};

export type DetermineWeightState = {
	inputValue: string | number;
	inputError: string | null;
	hasAcceptedWarning: boolean;
};

export default class DetermineWeight extends React.Component<DetermineWeightProps, DetermineWeightState> {
	public static displayName = 'AnamnesisDetermineWeight';
	public static defaultProps = {
		inputField: {
			delegate: noop,
		},
		actionButtonsActive: {},
		actionButtonsInactive: {},
		doSaveWeight: noop,
	};

	constructor(props: DetermineWeightProps) {
		super(props);

		this.state = {
			inputValue: props.weight ? props.weight : '',
			hasAcceptedWarning: false,
			inputError: null,
		};
	}

	public componentDidUpdate(prevProps: DetermineWeightProps) {
		if (this.props.weight !== prevProps.weight) {
			this.setState({
				inputValue: this.props.weight || '',
			});
		}
	}

	public isRecommended() {
		return this.state.inputError !== VALIDATE_IS_NOT_RECOMMENDED;
	}

	public isValidSubmission() {
		const { inputValue, inputError, hasAcceptedWarning } = this.state;

		const inacceptableValues = [VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_PENDING];

		if (inputValue === '') {
			return false;
		}

		if (inacceptableValues.includes(inputError!)) {
			return false;
		}

		if (inputError === VALIDATE_IS_NOT_RECOMMENDED && !hasAcceptedWarning) {
			return false;
		}

		return true;
	}

	public handleInputChange = (value: string | number) => {
		this.setState({
			inputValue: value || '',
			inputError: VALIDATE_IS_PENDING,
		});
	};

	public handleInputValidation = (error?: string) => {
		this.setState({
			inputError: error as string,
		});
	};

	public handleButtonClickComplete = () => {
		const _this = this;
		const { onStepCompleted } = this.props;

		onStepCompleted.apply(null, [
			{
				waitFor: 'savedWeight',
				actionCreator: _this.props.doSaveWeight,
				nextRoute: routes.anamnesis.defineHealthTarget,
				data: {
					weight: _this.state.inputValue,
					acceptedWeightWarning: _this.state.hasAcceptedWarning,
				},
			},
		]);
	};

	public onHandleButtonClicks = (type: string) => {
		const { onNavigationClick, onStepCompleted } = this.props;

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			this.handleButtonClickComplete();
			return;
		}

		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;
			onNavigationClick.apply(this, [routes.anamnesis.determineHeight]);
		}
	};

	public onChangeAcceptHint = (accepted: boolean) => {
		this.setState({
			hasAcceptedWarning: !!accepted,
		});
	};

	public renderWarning() {
		return (
			<Agreement
				title={i18n('react.impulsCoach.components.anamnesis.determineWeight.validation.interference.title')}
				text={i18n('react.impulsCoach.components.anamnesis.determineWeight.validation.interference.text')}
			>
				<ElementList>
					<Checkbox
						tag="li"
						id="anamnesis-pregnant-accept"
						name="anamnesis-pregnant-accept"
						label={i18n(
							'react.impulsCoach.components.anamnesis.determineWeight.validation.interference.acceptText'
						)}
						onChange={this.onChangeAcceptHint}
						modifier="white"
					/>
				</ElementList>
			</Agreement>
		);
	}

	public renderActiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsActive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);
	}

	public renderInactiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsInactive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);
	}

	public render() {
		const { inputValue, inputError } = this.state;

		return (
			<div>
				<Spacer size="m" />

				<ControlledInput
					id="weight"
					name="weight"
					unit={i18n('react.impulsCoach.components.anamnesis.determineWeight.unit')}
					placeholder={i18n('react.impulsCoach.components.anamnesis.determineWeight.placeholderText')}
					value={inputValue.toString()}
					onChange={this.handleInputChange as any}
					onValidationDone={this.handleInputValidation}
					disabled={this.props.isUpdatingAnamnesis}
					autoComplete={!isNativePlatformAndroid()}
				/>
				<Spacer size="l" />
				{inputError === VALIDATE_IS_NOT_ALLOWED ? leaveCoach() : null}
				{!this.isRecommended() ? this.renderWarning() : null}
				{this.isValidSubmission() ? this.renderActiveButtons() : this.renderInactiveButtons()}
			</div>
		);
	}
}
